import { supabase } from "@/integrations/supabase/client";

type EventName =
  | 'landing_cta_clicked'
  | 'free_quiz_started'
  | 'question_answered'
  | 'ai_tutor_opened'
  | 'quiz_completed'
  | 'user_upgraded'
  // Auth events
  | 'user_registered'
  | 'user_logged_in'
  | 'user_logged_out'
  // Real exam events
  | 'real_exam_started'
  | 'real_exam_completed'
  | 'real_exam_abandoned'
  | 'real_exam_resumed'
  // Practice events
  | 'practice_started'
  | 'level_passed'
  // Payment events
  | 'checkout_started'
  | 'payment_verified'
  // Institutional events
  | 'baseline_required'
  | 'baseline_started'
  | 'baseline_completed'
  | 'compliance_unlocked'
  | 'ai_explain_error'
  // Fase 0 — access & session telemetry
  | 'app_opened'
  | 'session_lost_unexpected'
  | 'session_invalidated_by_other_device'
  | 'session_validation_failed'
  | 'pwa_install_prompted'
  | 'pwa_installed'
  | 'version_mismatch_detected'
  | 'forced_reload_applied'
  // Review prompt
  | 'review_eligibility_evaluated'
  | 'review_prompt_queued'
  | 'review_modal_shown'
  | 'review_modal_dismissed'
  | 'review_modal_submit_started'
  | 'review_submitted'
  | 'review_prompt_suppressed'
  | 'review_prompt_error'
  // Subscription funnel instrumentation
  | 'subscription_cta_clicked'
  | 'paywall_shown'
  | 'paywall_cta_clicked'
  | 'checkout_completed'
  // Trial policy funnel (client side; server-side twins are written by stripe-webhook)
  | 'plan_selected'
  | 'checkout_abandoned'
  | 'trial_started'
  | 'trial_converted_to_paid'
  | 'trial_canceled'
  | 'trial_ended_without_payment'
  | 'admin_free_access_granted'
  | 'admin_free_access_revoked';

/** Commercial platform bucket: which billing rail applies to this runtime. */
export function getBillingPlatform(): 'web' | 'android_pwa' | 'ios_native' | 'android_native' {
  try {
    const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string } }).Capacitor;
    if (cap?.isNativePlatform?.()) {
      const p = cap.getPlatform?.();
      if (p === 'ios') return 'ios_native';
      if (p === 'android') return 'android_native';
    }
    if (/Android/i.test(navigator.userAgent)) return 'android_pwa';
  } catch {
    /* noop */
  }
  return 'web';
}

/**
 * Trial-policy cohort of the current user ('legacy' | 'new'), cached per session.
 * Lets us compare the old auto-trial model against the payment-method-first
 * model on the exact same events.
 */
let cachedPolicyCohort: string | null = null;
let policyCohortPromise: Promise<string | null> | null = null;

export async function getTrialPolicyCohort(): Promise<string | null> {
  if (cachedPolicyCohort) return cachedPolicyCohort;
  if (!policyCohortPromise) {
    policyCohortPromise = (async () => {
      try {
        const { data } = await supabase.rpc('get_my_trial_policy_cohort' as any);
        cachedPolicyCohort = (data as string) ?? null;
        return cachedPolicyCohort;
      } catch {
        return null;
      }
    })();
  }
  return policyCohortPromise;
}

/**
 * Fire-and-forget analytics event.
 * Never blocks UI, never throws.
 */
export function trackEvent(
  eventName: EventName,
  screen?: string,
  metadata?: Record<string, unknown>
) {
  // Use setTimeout to avoid blocking the current call stack
  setTimeout(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const policyCohort = session?.user ? await getTrialPolicyCohort() : null;

      await supabase.from('analytics_events' as any).insert({
        user_id: session?.user?.id ?? null,
        event_name: eventName,
        screen: screen ?? null,
        metadata: {
          platform: getBillingPlatform(),
          policy_cohort: policyCohort,
          ...(metadata ?? {}),
        },
      });
    } catch {
      // Silent fail — analytics must never break the app
    }
  }, 0);
}

